import React, { FC, useCallback, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { capitalize, startCase, toLower, drop } from 'lodash';
import { toast } from 'react-toastify';
import BoostedOffersFilters from 'components/BoostedOffers/BoostedOffersFilters/BoostedOffersFilters';
import BoostedOffersTable from 'components/BoostedOffers/BoostedOffersTable/BoostedOffersTable';
import { downloadCSV, exportJSONToCSV } from 'helpers';
import { formatLocalDateTime } from 'helpers/DateHelpers';
import { useGetBoostedOffersQuery, useDeleteBoostedOfferCampaignMutation, useEditBoostedOfferCampaignMutation } from 'reduxState/store/boostedOffers/api';
import { AppOption, BoostedOffer, CampaignsType, BoostedOfferSortBy } from 'reduxState/store/boostedOffers/types';
import { FUNDING_SOURCE_LABELS } from '../constants';
import { tableHeaders } from '../BoostedOffersTable/constants';

interface CampaignListProps {
  adminAppId: string;
  setCampaignToEdit: (campaign: BoostedOffer | null) => void;
  setIsEditingActiveCampaign: React.Dispatch<React.SetStateAction<boolean>>;
}

const BoostedOffersCampaignList: FC<CampaignListProps> = ({
  adminAppId,
  setCampaignToEdit,
  setIsEditingActiveCampaign,
}) => {
  const [appsToInclude, setAppsToInclude] = useState<AppOption[]>([]);
  const [campaignsType, setCampaignsType] = useState<CampaignsType>('present');
  const [sortBy, setSortBy] = useState<BoostedOfferSortBy>('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [isDeleteMode, setIsDeleteMode] = useState(false);
  const [campaignsToDelete, setCampaignsToDelete] = useState<BoostedOffer[]>([]);
  const [isBatchEditMode, setIsBatchEditMode] = useState(false);
  const [campaignsToEdit, setCampaignsToEdit] = useState<BoostedOffer[]>([]);
  const { user } = useAuth0();

  const [deleteBoostedOfferCampaign] = useDeleteBoostedOfferCampaignMutation();
  const [editBoostedOfferCampaign] = useEditBoostedOfferCampaignMutation();
  const { data, isFetching, isError } = useGetBoostedOffersQuery({
    sortBy,
    sortOrder,
    adminAppId,
    applicationIds: appsToInclude.map((app: AppOption) => app.id.toString()),
  });

  const sortTable = useCallback(
    (header: BoostedOfferSortBy): void => {
      if (sortBy === header) {
        setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
      } else {
        setSortBy(header);
        setSortOrder('desc');
      }
    },
    [sortBy, sortOrder],
  );

  const toggleCampaignToDelete = (campaign: BoostedOffer, shouldBeDeleted: boolean) => {
    if (shouldBeDeleted) {
      setCampaignsToDelete(Array.from(new Set([...campaignsToDelete, campaign])));
    } else {
      setCampaignsToDelete(campaignsToDelete.filter(campaignToDelete => campaignToDelete.ID !== campaign.ID));
    }
  };

  const toggleCampaignToEdit = (campaign: BoostedOffer, shouldBeEdited: boolean) => {
    if (shouldBeEdited) {
      setCampaignsToEdit(Array.from(new Set([...campaignsToEdit, campaign])));
    } else {
      setCampaignsToEdit(campaignsToEdit.filter(campaignToEdit => campaignToEdit.ID !== campaign.ID));
    }
  };

  const handleSelectAll = (shouldSelect: boolean) => {
    const currentCampaigns = data?.campaignsData?.[campaignsType] || [];
    if (shouldSelect) {
      // Add all current campaigns that aren't already selected
      const newCampaigns = currentCampaigns.filter(
        campaign => !campaignsToEdit.some(selected => selected.ID === campaign.ID)
      );
      setCampaignsToEdit([...campaignsToEdit, ...newCampaigns]);
    } else {
      // Remove all current campaigns from selection
      const currentCampaignIds = new Set(currentCampaigns.map(c => c.ID));
      setCampaignsToEdit(campaignsToEdit.filter(campaign => !currentCampaignIds.has(campaign.ID)));
    }
  };

  const exportToCsv = async (): Promise<void> => {
    if (!data?.campaignsData) return;
    const formattedCampaignsData = data.campaignsData[campaignsType].map(campaign => ({
      ...campaign,
      StartDateTime: formatLocalDateTime(campaign.StartDate, 'M/dd/yyyy haaa'),
      EndDateTime: formatLocalDateTime(campaign.EndDate, 'M/dd/yyyy haaa'),
      CreatedDate: formatLocalDateTime(campaign.CreatedDate, 'M/dd/yyyy'),
      FundedBy: FUNDING_SOURCE_LABELS[campaign.FundingSource],
    }));
    const formattedHeaders = tableHeaders
      .filter(header => !header.key.toLowerCase().includes('delete')) // Removes the hidden delete column
      .map(({ key, value }) => ({
        accessor: key as keyof BoostedOffer,
        name: value,
      }));

    const csv = exportJSONToCSV(formattedCampaignsData, formattedHeaders);

    if (csv) {
      const formattedAppsIncluded = appsToInclude.length
        ? `_${appsToInclude
            .map(({ label }) => {
              const labelStringArray = startCase(toLower(label)).split(' ');
              return drop(labelStringArray, 1).join('_');
            })
            .join('_')}`
        : '';
      downloadCSV(csv, `${capitalize(campaignsType)}_Boosted_Offers${formattedAppsIncluded}.csv`);
    } else {
      toast.error('There was an error while downloading boosted offer campaigns.');
    }
  };

  const deleteCampaigns = async () => {
    try {
      await Promise.all(campaignsToDelete.map(campaign => deleteBoostedOfferCampaign(campaign.ID)));
      toast.success('Campaigns deleted successfully!');
      setIsDeleteMode(false);
      setCampaignsToDelete([]);
    } catch (err) {
      toast.error(`There was an issue deleting one or more campaigns`);
      console.error(err);
    }
  };

  const batchUpdateEndDate = async (newEndDate: Date, note: string) => {
    if (!user?.email) {
      toast.error('User email is required to update campaigns');
      return;
    }

    if (campaignsToEdit.length === 0) {
      toast.error('Please select at least one campaign to update');
      return;
    }

    const modifiedAuthor = user.email; // Type narrowing after guard clause

    try {
      await Promise.all(
        campaignsToEdit.map(campaign =>
          editBoostedOfferCampaign({
            campaign: {
              ID: campaign.ID,
              ApplicationID: campaign.ApplicationID,
              MerchantID: campaign.MerchantID,
              Rate: Number(campaign.Rate),
              StartDate: campaign.StartDate,
              EndDate: newEndDate.toISOString(),
              ModifiedAuthor: modifiedAuthor,
              Kind: campaign.Kind,
              Note: note,
              FundingSource: campaign.FundingSource,
            },
          }).unwrap()
        )
      );
      toast.success(`Successfully updated end date for ${campaignsToEdit.length} campaign(s)!`);
      setIsBatchEditMode(false);
      setCampaignsToEdit([]);
    } catch (err: any) {
      const errorMessage = err?.data?.ErrorMessage || 'Failed to update one or more campaigns';
      toast.error(errorMessage);
      console.error(err);
    }
  };

  return (
    <>
      <BoostedOffersFilters
        campaignsType={campaignsType}
        setAppsToInclude={apps => {
          setAppsToInclude(apps);
        }}
        setCampaignsType={setCampaignsType}
        exportToCsv={exportToCsv}
        adminAppId={adminAppId}
        isDeleteMode={isDeleteMode}
        setIsDeleteMode={setIsDeleteMode}
        deleteCampaigns={deleteCampaigns}
        isBatchEditMode={isBatchEditMode}
        setIsBatchEditMode={setIsBatchEditMode}
        campaignsToEdit={campaignsToEdit}
        setCampaignsToEdit={setCampaignsToEdit}
        batchUpdateEndDate={batchUpdateEndDate}
      />
      <BoostedOffersTable
        campaignsData={data?.campaignsData || { past: [], present: [], future: [] }}
        campaignsType={campaignsType}
        error={isError ? 'Failed to fetch Boosted Offers.' : ''}
        isLoading={isFetching}
        isDeleteMode={isDeleteMode}
        isBatchEditMode={isBatchEditMode}
        sortBy={sortBy}
        sortOrder={sortOrder}
        sortTable={sortTable}
        toggleCampaignToDelete={toggleCampaignToDelete}
        toggleCampaignToEdit={toggleCampaignToEdit}
        campaignsToEdit={campaignsToEdit}
        setCampaignToEdit={setCampaignToEdit}
        setIsEditingActiveCampaign={setIsEditingActiveCampaign}
        onSelectAll={handleSelectAll}
      />
    </>
  );
};

export default BoostedOffersCampaignList;
